import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { locationsApi, type LocationData, type LocationInput } from '../api/locationsApi';
import { settingsKeys } from '../queryKeys';
import { Card } from '@/shared/ui/Card';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { Button } from '@/shared/ui/Button';
import { Badge } from '@/shared/ui/Badge';
import { useToast } from '@/shared/ui/Toast';
import { validatePhone, formatPhoneDisplay, PHONE_PLACEHOLDER } from '@/shared/utils/validation';
import { PRACTICE_TIME_ZONES, timeZoneName } from '@/shared/utils/timezones';
import styles from './SettingsPage.module.css';

const EMPTY_FORM: LocationInput = {
  name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  website: '',
  timezone: '',
};

/** "Pacific Time (America/Los_Angeles)". */
const zoneLabel = (zone: string) => `${timeZoneName(zone)} (${zone})`;

function LocationForm({
  initial,
  nameHint,
  addressTimezone = null,
  onSave,
  onCancel,
  isSaving,
  hideWebsite = false,
}: {
  initial: LocationInput;
  nameHint?: string;
  /** One location: the practice's website is the location's, edited on the practice card. */
  hideWebsite?: boolean;
  /** The zone the saved address is in (edit only), offered when the chosen one differs. */
  addressTimezone?: string | null;
  onSave: (data: LocationInput) => void;
  /** Absent: the form is always open, with no Cancel. */
  onCancel?: () => void;
  isSaving: boolean;
}) {
  const [form, setForm] = useState<LocationInput>(initial);
  const [phoneError, setPhoneError] = useState('');
  const set =
    (field: keyof LocationInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = () => {
    const err = validatePhone(form.phone ?? '');
    if (err) {
      setPhoneError(err);
      return;
    }
    // The form re-sends the address on every save, and the server re-derives the zone from an
    // address unless a zone comes with it. So the zone goes along unless the address itself
    // changed and the zone was left alone (then the new address decides); a new location sends
    // one only if someone picked it.
    const { timezone, ...rest } = form;
    const addressChanged = (['address', 'city', 'state', 'zip'] as const).some(
      (k) => (form[k] ?? '') !== (initial[k] ?? ''),
    );
    const send = !initial.timezone
      ? !!timezone
      : !!timezone && (timezone !== initial.timezone || !addressChanged);
    onSave(send ? { ...rest, timezone } : rest);
  };

  const isNew = !initial.timezone;
  const zones = [
    ...new Set([
      ...(initial.timezone ? [initial.timezone] : []),
      ...(addressTimezone ? [addressTimezone] : []),
      ...PRACTICE_TIME_ZONES,
    ]),
  ];
  const zoneOptions = [
    ...(isNew ? [{ value: '', label: 'From the address' }] : []),
    ...zones.map((z) => ({
      value: z,
      label: `${zoneLabel(z)}${z === addressTimezone ? ' · your address' : ''}`,
    })),
  ];
  const offerAddressZone = !!addressTimezone && form.timezone !== addressTimezone;

  return (
    <div className={styles.locationFormFields}>
      <Input
        label="Location Name"
        value={form.name}
        onChange={set('name')}
        hint={nameHint}
        fullWidth
      />
      <Input
        label="Phone"
        type="tel"
        value={form.phone ?? ''}
        onChange={(e) => {
          set('phone')(e);
          setPhoneError('');
        }}
        onBlur={(e) => setForm((p) => ({ ...p, phone: formatPhoneDisplay(e.target.value) }))}
        placeholder={PHONE_PLACEHOLDER}
        error={phoneError}
        fullWidth
      />
      {!hideWebsite && (
        <Input
          label="Website"
          value={form.website ?? ''}
          onChange={set('website')}
          placeholder="https://example.com"
          fullWidth
        />
      )}
      <Input
        label="Street Address"
        value={form.address ?? ''}
        onChange={set('address')}
        fullWidth
      />
      <div className={styles.cityStateZip}>
        <Input label="City" value={form.city ?? ''} onChange={set('city')} fullWidth />
        <Input label="State" value={form.state ?? ''} onChange={set('state')} fullWidth />
        <Input label="ZIP" value={form.zip ?? ''} onChange={set('zip')} fullWidth />
      </div>
      <Select
        label="Time zone"
        value={form.timezone ?? ''}
        onChange={set('timezone')}
        options={zoneOptions}
        hint={
          isNew
            ? 'Visit times, text timing and agent schedules use it. Leave it to come from the address, or pick one.'
            : 'Visit times, text timing and agent schedules use it. Changing the address updates it unless you pick one here.'
        }
        fullWidth
      />
      {offerAddressZone && (
        <div className={styles.zoneSuggestion}>
          <span>Your address is in {timeZoneName(addressTimezone)}.</span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setForm((p) => ({ ...p, timezone: addressTimezone }))}
          >
            Use {timeZoneName(addressTimezone)}
          </Button>
        </div>
      )}
      <div className={styles.formFooter}>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
        )}
        <Button variant="primary" loading={isSaving} onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  );
}

function LocationCard({
  loc,
  onDeleted,
  only = false,
}: {
  loc: LocationData;
  onDeleted: () => void;
  /** The practice's only location: its form stays open, without the website (the practice card has it). */
  only?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();

  const updateMutation = useMutation({
    mutationFn: (data: LocationInput) => locationsApi.update(loc.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.locations() });
      toast.success('Location updated.');
      setEditing(false);
    },
    onError: () => toast.error('Failed to update location.'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => locationsApi.delete(loc.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.locations() });
      toast.success('Location removed.');
      onDeleted();
    },
    onError: () => toast.error('Failed to delete location.'),
  });

  if (editing || only) {
    return (
      <Card title={only ? 'Address, phone & time zone' : undefined}>
        <LocationForm
          // Re-seeded when the server changes these underneath it: the zone from a new address, the name from a
          // practice rename.
          key={only ? `${loc.id}-${loc.timezone}-${loc.name}` : loc.id}
          hideWebsite={only}
          nameHint={
            only
              ? 'Renaming the practice renames this too, unless you give the location its own name.'
              : undefined
          }
          initial={{
            name: loc.name,
            address: loc.address,
            city: loc.city,
            state: loc.state,
            zip: loc.zip,
            phone: loc.phone,
            website: loc.website,
            timezone: loc.timezone,
          }}
          addressTimezone={loc.addressTimezone}
          onSave={(data) => updateMutation.mutate(data)}
          onCancel={only ? undefined : () => setEditing(false)}
          isSaving={updateMutation.isPending}
        />
      </Card>
    );
  }

  const addressLine = [loc.address, loc.city, loc.state, loc.zip].filter(Boolean).join(', ');

  return (
    <Card>
      <div className={styles.locationCard}>
        <div className={styles.locationInfo}>
          <div className={styles.locationName}>
            <h4>{loc.name}</h4>
            {loc.isDefault && (
              <Badge variant="success" size="sm">
                Primary
              </Badge>
            )}
          </div>
          {addressLine && <p className={styles.locationMeta}>{addressLine}</p>}
          {loc.phone && <p className={styles.locationMeta}>{loc.phone}</p>}
          {loc.website && <p className={styles.locationMeta}>{loc.website}</p>}
          {loc.timezone && (
            <p className={styles.locationMeta}>
              {timeZoneName(loc.timezone)}
              {loc.addressTimezone && loc.addressTimezone !== loc.timezone && (
                <>
                  {' '}
                  <Badge variant="warning" size="sm">
                    Address is in {timeZoneName(loc.addressTimezone)}
                  </Badge>
                </>
              )}
            </p>
          )}
        </div>
        <div className={styles.locationActions}>
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
          {!loc.isDefault && (
            <Button
              variant="ghost"
              size="sm"
              loading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              Delete
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * The practice's locations. With one, its address, phone and time zone are an open form under the practice card;
 * with more, a card per location to edit or remove, and the practice card keeps only what they share.
 */
export function LocationsSection({
  onLocations,
}: {
  /** Tells the page the locations, which decide the practice card's fields. */
  onLocations?: (locations: LocationData[]) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: settingsKeys.locations(),
    queryFn: () => locationsApi.getAll(),
  });

  const createMutation = useMutation({
    mutationFn: (payload: LocationInput) => locationsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.locations() });
      toast.success('Location added.');
      setAdding(false);
    },
    onError: () => toast.error('Failed to add location.'),
  });

  const locations = data?.data ?? [];
  const only = locations.length === 1;
  useEffect(() => {
    if (!isLoading && data) onLocations?.(data.data ?? []);
  }, [isLoading, data, onLocations]);

  return (
    <div className={styles.locationList}>
      {isLoading && (
        <Card>
          <p className={styles.loadingState}>Loading...</p>
        </Card>
      )}

      {!isLoading && locations.length === 0 && !adding && (
        <Card>
          <p className={styles.emptyState}>No locations found.</p>
        </Card>
      )}

      {locations.map((loc) => (
        <LocationCard key={loc.id} loc={loc} only={only} onDeleted={() => {}} />
      ))}

      {adding ? (
        <Card>
          <p className={styles.newLocationTitle}>New Location</p>
          <LocationForm
            initial={EMPTY_FORM}
            onSave={(data) => createMutation.mutate(data)}
            onCancel={() => setAdding(false)}
            isSaving={createMutation.isPending}
          />
        </Card>
      ) : (
        !isLoading && (
          <div>
            <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
              + Add a location
            </Button>
          </div>
        )
      )}
    </div>
  );
}
